import React, { useEffect, useRef, useState } from 'react'
import Style from '../summaryDetails.module.css'
import { entityIcon } from '../../../../../SharedComponents/EntityIcon/entityIcon'
import { formatTimestamp, parseTimestamp, relativeTime } from '../../../summaryFormat'
const COLUMN_COUNT = 10
export const isError = (record) => record.statusName.toLowerCase() === 'error'
const statusOf = (statusName) => {
    const name = statusName.toLowerCase()
    if (name === 'error') return { tone: 'danger', label: 'Failed' }
    if (name.indexOf('complete') !== -1) return { tone: 'success', label: statusName }
    return { tone: 'info', label: statusName || 'Unknown' }
}
const Empty = () => <span className={Style.muted}>—</span>
const IdCell = ({ value }) => (
    <td className={Style.idCell}>{value ? <span title={value}>{value}</span> : <Empty />}</td>
)

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false)
    if (!navigator.clipboard) return null
    return (
        <button
            type="button"
            className={Style.ghostButton}
            onClick={() =>
                navigator.clipboard.writeText(text).then(() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                })
            }
        >
            <i className={copied ? 'uil-check' : 'uil-copy'} aria-hidden="true" /> {copied ? 'Copied' : 'Copy'}
        </button>
    )
}

const NO_RESYNC_PERMISSION = "You don't have permission to re-sync records"

const resyncHint = (failed, canResync) => {
    if (!canResync) return NO_RESYNC_PERMISSION
    return failed ? 'Select for re-sync' : 'Only failed records can be re-synced'
}

const RecordRow = ({ record, canResync, isSelected, onToggle, isExpanded, onExpand }) => {
    const status = statusOf(record.statusName)
    const failed = isError(record)
    const updated = parseTimestamp(record.updatedTime)

    return (
        <>
            <tr className={[isSelected ? Style.rowSelected : '', isExpanded ? Style.rowExpanded : ''].join(' ')}>
                <td className={Style.checkCell}>
                    <input
                        type="checkbox"
                        className={Style.checkbox}
                        checked={isSelected}
                        disabled={!failed || !canResync}
                        onChange={onToggle}
                        aria-label={`Select message ${record.messageId} for re-sync`}
                        title={resyncHint(failed, canResync)}
                    />
                </td>
                <td>
                    <span className={Style.entity}>
                        <span className={Style.entityIcon}><i className={entityIcon(record.entityName)} aria-hidden="true" /></span>
                        {record.entityName}
                    </span>
                </td>
                <td className={Style.mono}>{record.messageId}</td>
                <IdCell value={record.sourceId} />
                <IdCell value={record.targetId} />
                <IdCell value={record.reference} />
                <td className={Style.statusCell}>
                    <span className={[Style.pill, Style[`pill_${status.tone}`]].join(' ')}>
                        <span className={Style.pillDot} aria-hidden="true" />
                        {status.label}
                    </span>
                    {failed && record.errorMessage ? (
                        <span className={Style.errorPreview} title={record.errorMessage}>{record.errorMessage}</span>
                    ) : null}
                </td>
                <td className={Style.timeCell}>
                    {updated ? (
                        <>
                            <span className={Style.timeRelative}>{relativeTime(updated)}</span>
                            <span className={Style.timeAbsolute}>{formatTimestamp(updated)}</span>
                        </>
                    ) : <Empty />}
                </td>
                <td className={Style.attemptCell}>
                    <span className={record.syncCounter > 1 ? Style.attemptsHigh : Style.attempts}>{record.syncCounter}</span>
                </td>
                <td className={Style.expandCell}>
                    {failed && record.errorMessage ? (
                        <button
                            type="button"
                            className={Style.expandButton}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Hide error details' : 'Show error details'}
                            onClick={onExpand}
                        >
                            <i className={isExpanded ? 'uil-angle-up' : 'uil-angle-down'} aria-hidden="true" />
                        </button>
                    ) : null}
                </td>
            </tr>
            {isExpanded ? (
                <tr className={Style.detailRow}>
                    <td colSpan={COLUMN_COUNT}>
                        <div className={Style.errorBox}>
                            <div className={Style.errorBoxHeader}>
                                <span><i className="uil-exclamation-octagon" aria-hidden="true" /> Error details · message {record.messageId}</span>
                                <CopyButton text={record.errorMessage} />
                            </div>
                            <pre className={Style.errorText}>{record.errorMessage}</pre>
                        </div>
                    </td>
                </tr>
            ) : null}
        </>
    )
}

const SkeletonRows = ({ count }) =>
    Array.from({ length: count }).map((_, index) => (
        <tr key={index}>
            {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                <td key={cell}><span className={Style.skeleton} style={{ width: cell === 0 || cell === 9 ? 14 : '60%' }} /></td>
            ))}
        </tr>
    ))

const StateRow = ({ icon, iconClass, text, hint, children }) => (
    <tr>
        <td colSpan={COLUMN_COUNT} className={Style.stateCell}>
            <i className={`${icon} ${iconClass}`} aria-hidden="true" />
            <p className={Style.stateText}>{text}</p>
            {hint ? <p className={Style.stateHint}>{hint}</p> : null}
            {children}
        </td>
    </tr>
)

const RecordsTable = ({ records, canResync, isLoading, loadError, onRetry, selected, setSelected, toggleSelected, hasFilters, onClearFilters, pageSize }) => {
    const [expanded, setExpanded] = useState(null)
    const selectAllRef = useRef(null)

    const selectable = canResync ? records.filter(isError).map((record) => record.messageId) : []
    const allSelected = selectable.length > 0 && selectable.every((id) => selected.indexOf(id) !== -1)
    const someSelected = selected.length > 0 && !allSelected

    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected
    }, [someSelected])

    useEffect(() => {
        setExpanded(null)
    }, [records])

    const renderBody = () => {
        if (isLoading) return <SkeletonRows count={Math.min(pageSize, 8)} />
        if (loadError) {
            return (
                <StateRow icon="uil-exclamation-octagon" iconClass={Style.stateIconError} text={loadError}>
                    <button type="button" className={Style.primaryButton} onClick={onRetry}>Retry</button>
                </StateRow>
            )
        }
        if (records.length === 0) {
            return hasFilters ? (
                <StateRow icon="uil-search" iconClass={Style.stateIcon} text="No records match these filters" hint="Try a wider date range or remove an ID filter.">
                    <button type="button" className={Style.ghostButton} onClick={onClearFilters}>Clear filters</button>
                </StateRow>
            ) : (
                <StateRow icon="uil-inbox" iconClass={Style.stateIcon} text="No records here" hint="Nothing has synced with this status yet." />
            )
        }
        return records.map((record) => (
            <RecordRow
                key={record.messageId}
                record={record}
                canResync={canResync}
                isSelected={selected.indexOf(record.messageId) !== -1}
                onToggle={() => toggleSelected(record.messageId)}
                isExpanded={expanded === record.messageId}
                onExpand={() => setExpanded(expanded === record.messageId ? null : record.messageId)}
            />
        ))
    }

    return (
        <div className={Style.tableScroll}>
            <table className={Style.table}>
                <thead>
                    <tr>
                        <th className={Style.checkCell}>
                            <input
                                ref={selectAllRef}
                                type="checkbox"
                                className={Style.checkbox}
                                checked={allSelected}
                                disabled={isLoading || selectable.length === 0}
                                onChange={() => setSelected(allSelected ? [] : selectable)}
                                aria-label="Select all failed records on this page"
                                title={selectable.length === 0 ? 'No failed records on this page' : 'Select all failed records on this page'}
                            />
                        </th>
                        <th>Entity</th>
                        <th>Message ID</th>
                        <th>Source ID</th>
                        <th>Target ID</th>
                        <th>Reference</th>
                        <th>Status</th>
                        <th>Last updated</th>
                        <th className={Style.attemptCell} title="Sync counter: how many times this record has been sent">Attempts</th>
                        <th className={Style.expandCell}><span className="sr-only">Details</span></th>
                    </tr>
                </thead>
                <tbody>{renderBody()}</tbody>
            </table>
        </div>
    )
}

export default RecordsTable
